import { FC, useEffect, useMemo } from 'react'
import { Form, json, useActionData, useNavigate } from 'react-router-dom'

// COMPONENTS
import { Column } from '@components/templates/column'
import CurrencyTextField from '@components/molecules/currency-textfield'
import Button from '@components/atoms/button'

// TYPES
import type { RouteObject } from 'react-router-dom'

// UTILITIES
import { createResponseObj, VirtualCardAPI } from '@apis'
import { createAction } from '@utils/actions'
import { isNonEmptyString, isNumber } from '@utils/type-checkers'
import { toPennies } from '@utils/number-helper'
import { useCurrencyInput } from '@hooks/inputs'
import { useGroupLoaderData } from '../../'


const formId = "edit-target-amount"
const Component: FC<{}> = () => {
    const group = useGroupLoaderData()
    const [targetAmount, targetAmountProps] = useCurrencyInput({
        initValue: `${(group.targetAmount || 0) / 100}`,
        max: 9999999,
    })
    const navigate = useNavigate()
    const actionData = useActionData()
    const submitable = useMemo(() => (!!targetAmount && toPennies(targetAmount) >= 100 && toPennies(targetAmount) <= 9999999), [targetAmount])

    useEffect(() => {
        if (actionData === 'success') handleBack()
    }, [actionData])

    function handleBack() {
        navigate(-1)
    }

    return (
        <Form id={formId} method="POST" className="flex flex-col" style={{ gap: 'inherit' }}>
            <Column.Header.Start>
                <a onClick={handleBack}>
                    <img src="/icons/arrow-left.svg" alt="back" style={{ width: '1.4rem', height: '1.4rem' }} draggable="false" />
                </a>
            </Column.Header.Start>
            <div className='flex flex-col gap-4'>
                <h1 className='text-blue-700 text-center'>{group.title}</h1>
                <h4 className='text-gray-500' style={{ fontWeight: '400', textAlign: 'center' }}>Update Goal Amount</h4>
            </div>
            <div className="flex flex-col" style={{ gap: '0.75rem', padding: '1rem' }}>
                <p className='text-body'>Enter in the estimated total amount you will need for this purchase. Make sure to <strong>overestimate</strong> for:</p>
                <ul style={{ paddingLeft: '2rem', listStyleType: 'disc' }}>
                    <li><p>Temporary holds</p></li>
                    <li><p>Merchant fees not disclosed until checkout</p></li>
                    <li><p>Tips</p></li>
                    <li><p>Applicable state and local taxes</p></li>
                </ul>
            </div>
            <CurrencyTextField style={{ flex: 1 }} centered={true} {...targetAmountProps} name="targetAmount" />
            <Column.Footer>
                <Button form={formId} type="submit" disabled={!submitable}>Update</Button>
            </Column.Footer>
        </Form>
    )
}

const action = createAction(async ({ request, params }) => {
    const formData = await request.formData()
    const groupId = params['groupId']
    const targetAmount = toPennies(`${formData.get('targetAmount')}` || '')

    console.log("Update Pledge Data:", { groupId, targetAmount })
    if (!isNonEmptyString(groupId) || (!isNumber(targetAmount) || targetAmount < 100)) throw createResponseObj({
        status: 'failed', type: 'local', name: 'missing-resources',
        message: `Missing required resources. ${JSON.stringify({ groupId, targetAmount })}`
    })

    await VirtualCardAPI.UpdateGroupRequest.fetch(groupId, { targetAmount })
    return json('success', { status: 200 })
})

const TargetRoute: RouteObject = {
    path: 'target',
    Component,
    action,
}

export { TargetRoute }
